/**
 * Roboto exception hierarchy. Shadows `packages.roboto.exceptions`.
 */

export interface RobotoServiceErrorBody {
  /**
   * The name of the error from `packages.roboto.exceptions`.
   *
   * This should match exactly with the name of the exception class to be instantiated.
   * If no such exception exists, the string value of the status code may be used
   * as a fallback for human readability.
   */
  error_code: string;
  message: string;
  stack_trace?: string[];
  resource_name?: string; // Only present for RobotoLimitExceededException
  limit_quantity?: number; // Only present for RobotoLimitExceededException
  current_quantity?: number; // Only present for RobotoLimitExceededException
}

/**
 * Base class for Roboto domain exceptions.
 *
 * All subclasses must define a public static `name` property
 * that maps to its corresponding error's name in the `roboto.exceptions` package.
 * This mapping is used to construct the appropriate exception type when handling HTTP responses.
 *
 * Each must also define a public static `httpStatusCode` that maps to the
 * corresponding error's status_code in the Python source.
 *
 * The base class uses 999 as its status code to prevent accidental matches with valid HTTP status codes.
 */
export class RobotoDomainException extends Error {
  public static httpStatusCode: number = 999;
  public static subclasses: (typeof RobotoDomainException)[] = [];

  protected errorBody: RobotoServiceErrorBody;

  public static fromHttpResponse(
    errorResponse: RobotoServiceErrorBody,
  ): RobotoDomainException {
    for (const subclass of RobotoDomainException.subclasses) {
      if (subclass.name === errorResponse.error_code) {
        return new subclass(errorResponse);
      }
    }

    return new RobotoDomainException(errorResponse);
  }

  public static fromStatusCode(statusCode: number): RobotoDomainException {
    const filtered = RobotoDomainException.subclasses.filter(
      (subclass) => subclass.httpStatusCode === statusCode,
    );

    // If there is exactly one match, disambiguate the status code to the exact match.
    if (filtered.length === 1) {
      const subclass = filtered[0];
      return new subclass({
        error_code: subclass.name,
        message: subclass.name,
      });
    }

    // Otherwise, don't guess what type of exception this is and just use the status code.
    return new RobotoDomainException({
      error_code: String(statusCode),
      message: "Error",
    });
  }

  constructor(errorBody: RobotoServiceErrorBody) {
    super(errorBody.message);
    this.name = "RobotoDomainException";
    this.errorBody = errorBody;
  }

  public get message(): string {
    return this.errorBody.message;
  }

  public get stackTrace(): string[] | undefined {
    return this.errorBody.stack_trace;
  }

  public isClientError(): boolean {
    const is4xx = (code: number) => 399 < code && code < 500;
    // By default, error_code is the name of a roboto exception. Check known exceptions for their numeric status code.
    for (const subclass of RobotoDomainException.subclasses) {
      if (
        is4xx(subclass.httpStatusCode) &&
        this.errorBody.error_code === subclass.name
      ) {
        return true;
      }
    }
    // As a fallback, error_code can be the http status code. Check if its numeric value is a 4XX status.
    return is4xx(Number(this.errorBody.error_code));
  }
}

export class RobotoUnauthorizedException extends RobotoDomainException {
  public static httpStatusCode = 401;
  public static name = "RobotoUnauthorizedException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoUnauthorizedException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoUnauthorizedException);

export class RobotoNotFoundException extends RobotoDomainException {
  public static httpStatusCode = 404;
  public static name = "RobotoNotFoundException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoNotFoundException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoNotFoundException);

export class RobotoDatasetNotFoundException extends RobotoNotFoundException {
  public static httpStatusCode = 404;
  public static name = "RobotoDatasetNotFoundException";

  constructor(...args: ConstructorParameters<typeof RobotoNotFoundException>) {
    super(...args);
    this.name = RobotoDatasetNotFoundException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoDatasetNotFoundException);

export class RobotoDeviceNotFoundException extends RobotoNotFoundException {
  public static httpStatusCode = 404;
  public static name = "RobotoDeviceNotFoundException";

  constructor(...args: ConstructorParameters<typeof RobotoNotFoundException>) {
    super(...args);
    this.name = RobotoDeviceNotFoundException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoDeviceNotFoundException);

export class RobotoIllegalArgumentException extends RobotoDomainException {
  public static httpStatusCode = 400;
  public static name = "RobotoIllegalArgumentException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoIllegalArgumentException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoIllegalArgumentException);

export class RobotoInvalidRequestException extends RobotoDomainException {
  public static httpStatusCode = 400;
  public static name = "RobotoInvalidRequestException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoInvalidRequestException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoInvalidRequestException);

export class RobotoInvalidStateTransitionException extends RobotoDomainException {
  public static httpStatusCode = 400;
  public static name = "RobotoInvalidStateTransitionException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoInvalidStateTransitionException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoInvalidStateTransitionException);

export class RobotoNoOrgProvidedException extends RobotoDomainException {
  public static httpStatusCode = 400;
  public static name = "RobotoNoOrgProvidedException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoNoOrgProvidedException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoNoOrgProvidedException);

export class RobotoConditionException extends RobotoDomainException {
  public static httpStatusCode = 409;
  public static name = "RobotoConditionException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoConditionException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoConditionException);

export class RobotoConflictException extends RobotoDomainException {
  public static httpStatusCode = 409;
  public static name = "RobotoConflictException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoConflictException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoConflictException);

export class RobotoLayoutConflictException extends RobotoConflictException {
  public static httpStatusCode = 409;
  public static name = "RobotoLayoutConflictException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoLayoutConflictException.name;
  }

  /**
   * The conflicting layouts that caused the exception.
   *
   * This is typed as `unknown[]` to avoid importing dependencies. Callers should
   * validate the array is a `LayoutRecord[]` before using.
   */
  public get conflictingLayouts(): unknown[] {
    const errorBody = this.errorBody as RobotoServiceErrorBody & {
      conflicting_layouts?: unknown[];
    };
    return errorBody.conflicting_layouts ?? [];
  }
}
RobotoDomainException.subclasses.push(RobotoLayoutConflictException);

/**
 * A dashboard definition write was built on a stale copy: someone replaced the
 * definition after the requester loaded it, so applying the write would erase
 * their edit.
 *
 * Resolved on `error_code` (the server sends the class name), not on the status:
 * three subclasses now claim 409, so `fromStatusCode` deliberately refuses to
 * disambiguate. That is fine — only a parsed response body carries the payload
 * below, and that is exactly the case where narrowing matters.
 */
export class RobotoDashboardRevisionConflictException extends RobotoConflictException {
  public static httpStatusCode = 409;
  public static name = "RobotoDashboardRevisionConflictException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoDashboardRevisionConflictException.name;
  }

  /**
   * The server's dashboard record as of the locked read that rejected the write,
   * so its definition and revision are guaranteed mutually consistent.
   *
   * Typed `unknown` to keep domain types out of the http layer; callers must
   * validate with `isDashboardRecord` before use.
   */
  public get currentDashboard(): unknown {
    const errorBody = this.errorBody as RobotoServiceErrorBody & {
      current_dashboard?: unknown;
    };
    return errorBody.current_dashboard;
  }
}
RobotoDomainException.subclasses.push(RobotoDashboardRevisionConflictException);

export class RobotoServiceException extends RobotoDomainException {
  public static httpStatusCode = 500;
  public static name = "RobotoServiceException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoServiceException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoServiceException);

export class RobotoUnknownOperationException extends RobotoDomainException {
  public static httpStatusCode = 404;
  public static name = "RobotoUnknownOperationException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoUnknownOperationException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoUnknownOperationException);

export class RobotoLimitExceededException extends RobotoDomainException {
  public static httpStatusCode = 403;
  public static name = "RobotoLimitExceededException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoLimitExceededException.name;
  }

  public get resourceName(): string {
    if (!this.errorBody.resource_name) {
      throw new Error("Resource name is not present");
    }
    return this.errorBody.resource_name;
  }

  public get limitQuantity(): number {
    if (this.errorBody.limit_quantity === undefined) {
      throw new Error("Limit quantity is not present");
    }
    return this.errorBody.limit_quantity;
  }

  public get currentQuantity(): number {
    if (this.errorBody.current_quantity === undefined) {
      throw new Error("Current quantity is not present");
    }
    return this.errorBody.current_quantity;
  }
}
RobotoDomainException.subclasses.push(RobotoLimitExceededException);

/**
 * The request uses a feature that is not enabled for the caller or on this
 * deployment, such as a route gated by a feature flag.
 */
export class RobotoFeatureNotAvailableException extends RobotoDomainException {
  public static httpStatusCode = 403;
  public static name = "RobotoFeatureNotAvailableException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoFeatureNotAvailableException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoFeatureNotAvailableException);

export class RobotoInternalException extends RobotoDomainException {
  public static httpStatusCode = 500;
  public static name = "RobotoInternalException";

  constructor(...args: ConstructorParameters<typeof RobotoDomainException>) {
    super(...args);
    this.name = RobotoInternalException.name;
  }
}
RobotoDomainException.subclasses.push(RobotoInternalException);
